import { Fragment } from "react";

import { findFeedbackByLanguage } from "@memorang/helpers/feedback";
import type { Choice, ItemFeedback, Media } from "@memorang/types";
/* -----------------Icons--------------- */
import Icon from "@react-native-vector-icons/material-design-icons/static";
import type { FontSchemaType } from "../../font";
import { useAppTheme } from "../../useAppTheme";
/* -----------------Components--------------- */
import Box from "../Box";
import { CustomListSubheader } from "../CustomListSubheader";
import ImagesCarousel from "../ImagesCarousel";
import RenderMarkdown from "../MarkdownRenderer";
import { getSortedChoices } from "./helpers";

/* -----------------Props--------------- */
type Props = {
	choices: Choice[];
	itemFeedback?: ItemFeedback;
	fontSize: number;
	appFont: FontSchemaType;
	renderTtsContainer?: (explanation?: string) => React.ReactNode;
	language: string;
	explanationTitle: string;
};

const RenderExplanation = ({
	explanation,
	fontSize,
	appFont,
	media,
}: {
	explanation: string | undefined;
	fontSize: number;
	appFont: FontSchemaType;
	media?: Media[];
}) => {
	if (explanation) {
		const normalizedExplanation = explanation.replace(/<br>/g, "\n\n");
		return (
			<Box>
				<RenderMarkdown
					text={normalizedExplanation}
					variant="explanation"
					fontSize={fontSize}
					paragraphMarginVertical={6}
					appFont={appFont}
				/>
				{media?.length ? <ImagesCarousel images={media} /> : null}
			</Box>
		);
	}
	return null;
};

/* -----------------Component--------------- */
const StaticExplanation = (props: Props) => {
	const {
		choices,
		itemFeedback,
		appFont,
		fontSize,
		renderTtsContainer,
		language,
		explanationTitle,
	} = props;

	const sortedChoices = getSortedChoices(choices);
	const correctChoices = sortedChoices.filter(({ isCorrect }) => isCorrect);

	const hasChoicesWithText = choices.some(({ text }) => text);

	const theme = useAppTheme();

	const feedbackEntry = findFeedbackByLanguage(itemFeedback);
	const feedbackDetailedText = feedbackEntry?.detailed?.text;

	const choiceExplanation =
		language === "zh-CN" && correctChoices[0]?.explanation_mandarin
			? correctChoices[0].explanation_mandarin
			: (correctChoices[0]?.explanation ?? "");

	const explanation = feedbackDetailedText || choiceExplanation;

	const RenderChoice = ({ isCorrect, text }: Choice) => (
		<Box display="flex" flexDirection="row" gap={8} alignItems="center">
			{isCorrect ? (
				<Icon
					name="checkbox-marked-circle"
					color={theme.palette.progress.high}
					size={24}
				/>
			) : (
				<Icon
					name="close-circle"
					size={24}
					color={theme.palette.progress.low}
				/>
			)}
			<Box flex={1}>
				<RenderMarkdown
					text={text}
					customStyles={{
						paragraph: {
							...theme.typography.fonts.medium,
						},
					}}
					fontSize={fontSize}
					variant="answer"
					appFont={appFont}
				/>
			</Box>
		</Box>
	);
	const ChoicesWithExplanation = () =>
		feedbackDetailedText ? (
			<RenderExplanation
				explanation={feedbackDetailedText}
				fontSize={fontSize}
				appFont={appFont}
				media={feedbackEntry?.detailed?.media}
			/>
		) : (
			<Box gap={8}>
				{sortedChoices.map((choice) => (
					<Fragment key={choice.id}>
						{hasChoicesWithText ? <RenderChoice {...choice} /> : null}
						<RenderExplanation
							explanation={
								language === "zh-CN" && choice.explanation_mandarin
									? choice.explanation_mandarin
									: (choice.explanation ?? "")
							}
							fontSize={fontSize}
							appFont={appFont}
						/>
					</Fragment>
				))}
			</Box>
		);

	if (correctChoices.length > 1) {
		return (
			<Box>
				<Box
					flexDirection="row"
					alignItems="center"
					justifyContent="space-between"
				>
					<CustomListSubheader
						enableGutters={false}
						font={appFont}
						textVariant="titleMedium"
						dense
					>
						{explanationTitle}
					</CustomListSubheader>
					{explanation ? renderTtsContainer?.(explanation) : null}
				</Box>

				<Box gap={8}>
					{correctChoices.map((choice) => (
						<Box key={choice.id}>
							<RenderChoice {...choice} />
						</Box>
					))}
					{explanation ? (
						<RenderExplanation
							explanation={explanation}
							fontSize={fontSize}
							appFont={appFont}
							media={feedbackEntry?.detailed?.media}
						/>
					) : null}
				</Box>
			</Box>
		);
	}

	return (
		<Box>
			<Box
				flexDirection="row"
				alignItems="center"
				justifyContent="space-between"
			>
				<CustomListSubheader
					enableGutters={false}
					font={appFont}
					textVariant="titleMedium"
					dense
				>
					{explanationTitle}
				</CustomListSubheader>
				{explanation ? renderTtsContainer?.(explanation) : null}
			</Box>
			<ChoicesWithExplanation />
		</Box>
	);
};

export default StaticExplanation;
